// This is the client's API it uses for interacting with the `server`.
import axios, { AxiosError, isAxiosError } from "axios";

import { getPasswordErrorMessages } from "../helpers/password-errors";
import { PreclassCheckResultType } from "../shared/types/preclass-check";

import { CSRF_TOKEN_HEADER, SERVER_API_PATH } from "./api-constants";
import { ImportExternalSectionResponseType } from "./api-data-types";

import type {
  BulkImportType,
  ClassUserType,
  CourseGroupingUserType,
  CourseUserType,
  CsrfTokenType,
  FormattedValidationError,
  ImportSectionRequestType,
  InviteKeySectionType,
  OrgType,
  OutcomeSubgroupType,
  OutcomeType,
  PageAccessDataType,
  SectionInviteType,
  SectionType,
  UserInformationType,
  UserType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { PasswordErrorKey } from "i18n/i18next";
import { slices, store } from "store/index";
import { getReadableAxiosError } from "utils/alerts";
import { reportApiFailure, withApiErrorReporting } from "utils/api-errors";
import { getCsrfToken } from "utils/auth";
import { logInfoWithDebugFlag } from "utils/logger";

export const serverApi = axios.create({
  baseURL: window._env_?.SERVER_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

serverApi.interceptors.request.use((config) => {
  const state = store.getState();
  // Note: we can't use the selector here because in some cases the
  // store may not be initialized yet.
  if (state?.view?.userIsInactive) {
    // Abort the request instead of throwing an error to prevent it from reaching rollbar
    const method = config.method?.toUpperCase() || "UNKNOWN";
    const url = config.url || "UNKNOWN";
    logInfoWithDebugFlag("debug_sockets", "Canceling request due to inactive user", {
      method,
      url,
    });
    const abortController = new AbortController();
    abortController.abort();
    // eslint-disable-next-line no-param-reassign
    config.signal = abortController.signal;
  }

  if (!config.headers[CSRF_TOKEN_HEADER]) {
    const csrfToken = getCsrfToken();
    // eslint-disable-next-line no-param-reassign
    config.headers[CSRF_TOKEN_HEADER] = csrfToken;
  }

  return config;
});

export function serverLogout() {
  return serverApi.post(`${SERVER_API_PATH}/auth/logout/`);
}

// All server API calls should have the `server` prefix to clarify that it's going to the `server`
// (as opposed to `collaboration`).

export interface ServerApiUserResponseType {
  user_id: string;
  collaboration_jwt: string;
  // See store/auth.ts for how this is used
  section_to_org_id_map: Record<string, string>;
}

export async function serverGetUser(): Promise<ServerApiUserResponseType> {
  // We don't catch/report an error here because this is a common call that will fail
  // if a user is just not logged in. Handled in detail further upstream.
  const response = await serverApi.get<ServerApiUserResponseType>(`${SERVER_API_PATH}/users/me/`, {
    withCredentials: true,
  });
  return response.data;
}

export type PasswordType = {
  current_password: string;
  password: string;
  confirm_password: string;
};

export type PasswordResetRequestType = {
  token: string;
  new_password: string;
  confirm_password: string;
};

export type RegistrationRequestType = {
  token: string;
  new_password: string;
  confirm_password: string;
  first_name: string;
  last_name?: string;
  eula_accepted: boolean;
};

export interface ServerApiDetailResponseType {
  detail: string;
}

export async function serverSetPassword(
  passwords: PasswordType,
): Promise<ServerApiDetailResponseType> {
  // We don't do any error handling here; it's handled in the calling function.

  const response = await serverApi.post<ServerApiDetailResponseType>(
    `${SERVER_API_PATH}/auth/users/set_password/`,
    {
      ...passwords,
    },
  );

  return response.data;
}

export async function serverRequestResetPassword(email: string) {
  try {
    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/request_password_reset/`,
      {
        email,
      },
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const code = error.response?.status;

      let message: string;
      // TODO: Double check that the client is requiring email field before this request is sent.
      if (code === 400) {
        message = t("error.toasts.missing_field", { item: "Email" });
      } else {
        message = getReadableAxiosError(error);
      }

      reportApiFailure("serverRequestResetPassword", error, message);

      throw new Error(message);
    } else {
      reportApiFailure("serverRequestResetPassword", error);
    }

    throw error;
  }
}

export async function serverResetPassword(
  token: string,
  newPassword: string,
  confirmPassword: string,
) {
  try {
    const requestData: PasswordResetRequestType = {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    };

    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/reset_password/`,
      requestData,
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const code = error.response?.status;

      let message = "";
      if (code === 400) {
        const data = error.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!data) {
          reportApiFailure("serverResetPassword", error);
          return;
        }

        for (const { details, t: translationKeys } of Object.values(data)) {
          message += getPasswordErrorMessages(translationKeys as PasswordErrorKey[], details);
        }

        if (!message) {
          message = t("error.error");
        }
      } else if (code === 403) {
        message = t("error.toasts.token_invalid");
      } else {
        message = getReadableAxiosError(error);
      }

      reportApiFailure("serverResetPassword", error, message);

      throw new Error(message);
    } else {
      reportApiFailure("serverResetPassword", error);
    }

    throw error;
  }
}

export async function serverRegisterUser(
  token: string,
  newPassword: string,
  confirmPassword: string,
  firstName: string,
  lastName: string,
  eulaAccepted: boolean,
) {
  try {
    const requestData: RegistrationRequestType = {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
      first_name: firstName,
      last_name: lastName,
      eula_accepted: eulaAccepted,
    };

    const response = await serverApi.post<ServerApiUserResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/register/`,
      requestData,
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const code = error.response?.status;

      let message = "";
      if (code === 400) {
        const data = error.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!data) {
          reportApiFailure("serverRegisterUser", error);
          return;
        }

        for (const { details, t: translationKeys } of Object.values(data)) {
          message += getPasswordErrorMessages(translationKeys as PasswordErrorKey[], details);
        }

        if (!message) {
          message = getReadableAxiosError(error);
        }
      } else if (code === 403) {
        message = t("error.toasts.token_invalid");
      } else {
        message = getReadableAxiosError(error);
      }

      reportApiFailure("serverRegisterUser", error, message);

      throw new Error(message);
    } else {
      reportApiFailure("serverRegisterUser", error);
    }

    throw error;
  }
}

export interface OnboardingDataResponseType {
  email: string;
  first_name: string;
  last_name: string;
  org_name: string;
  pending_system_check: boolean;
}

export async function getOnboardingData(
  token: string,
  orgId: string,
): Promise<OnboardingDataResponseType> {
  try {
    const response = await serverApi.get<OnboardingDataResponseType>(
      `${SERVER_API_PATH}/auth/password_reset/get_onboarding_data/`,
      {
        params: { token, org_id: orgId },
      },
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const code = error.response?.status;

      // The server responds with a 422 to indicate that the user has already completed
      // onboarding. Rethrow the error to be caught and handled in OnboardingView.
      if (code === 422) {
        throw error;
      }

      let message = "";
      if (code === 400) {
        message = t("error.toasts.missing_field", { item: "Token or Organization ID" });
      } else if (code === 403) {
        const responseData = error.response?.data as
          | Partial<ServerApiDetailResponseType>
          | undefined;
        message = responseData?.detail || t("user_options_menu.redeem.unknown_error");
      } else {
        message = getReadableAxiosError(error);
      }

      reportApiFailure("getOnboardingData", error, message);
      throw new Error(message);
    } else {
      reportApiFailure("getOnboardingData non-axios", error);
    }

    throw error;
  }
}

export async function switchOrg(newOrgId: string) {
  return withApiErrorReporting("switchOrg", async () => {
    return serverApi.post<OrgType>(`${SERVER_API_PATH}/orgs/orgs/${newOrgId}/switch/`);
  });
}

export async function serverManageCourseAdmin(
  userId: string,
  courseIds: string[],
  courseGroupingIds: string[] = [],
): Promise<{ course_users: CourseUserType[]; course_grouping_users: CourseGroupingUserType[] }> {
  return withApiErrorReporting("serverManageCourseAdmin", async () => {
    const response = await serverApi.post<{
      course_users: CourseUserType[];
      course_grouping_users: CourseGroupingUserType[];
    }>(`${SERVER_API_PATH}/material/course_users/manage/`, {
      user_id: userId,
      course_ids: courseIds,
      course_grouping_ids: courseGroupingIds,
    });
    return response.data;
  });
}

export type RegistrationType = {
  token: string;
  org_id: string;
};

export interface EulaError extends Error {
  name: "EULA_NOT_ACCEPTED";
  token?: string;
  orgId?: string;
}

export async function serverLogin(email: string, password: string) {
  try {
    const response = await serverApi.get<CsrfTokenType>(`${SERVER_API_PATH}/auth/csrf/`);
    const csrftoken = response.data.csrftoken;
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    const headers = {
      "Content-Type": "application/x-www-form-urlencoded",
      [CSRF_TOKEN_HEADER]: csrftoken,
    };

    await serverApi.post(`${SERVER_API_PATH}/auth/login/`, formData, {
      headers,
    });
  } catch (err) {
    // Handle 422 case (EULA not accepted, redirect to onboarding)
    const error = err as AxiosError<RegistrationType>;
    if (error?.response?.status === 422) {
      const responseData = error.response.data;
      const eulaError = new Error("EULA_NOT_ACCEPTED") as EulaError;

      eulaError.name = "EULA_NOT_ACCEPTED";
      eulaError.token = responseData.token;
      eulaError.orgId = responseData.org_id;

      throw eulaError;
    }

    // Don't bother reporting login failures due to Unauthorized (likely just wrong credentials provided by user).
    if (error.response?.status !== 401) {
      reportApiFailure("serverLogin", error);
    }

    throw error;
  }
}

export async function serverCreateImpersonationUrl(targetUserId: string | null): Promise<string> {
  if (!targetUserId) {
    throw new Error("Target user ID is required");
  }
  return withApiErrorReporting("serverCreateImpersonationUrl", async () => {
    const response = await serverApi.post<{
      redemption_url: string;
    }>(`${SERVER_API_PATH}/auth/impersonation_session/`, { target_user_id: targetUserId });
    return response.data.redemption_url;
  });
}

export async function serverGetSectionInvite(sectionId: string): Promise<SectionInviteType[]> {
  return withApiErrorReporting("serverGetSectionInvite", async () => {
    const response = await serverApi.get<SectionInviteType[]>(
      `${SERVER_API_PATH}/auth/section_invite/?section_id=${sectionId}`,
    );
    return response.data;
  });
}

export async function serverCreateSectionInvite(sectionId: string): Promise<SectionInviteType> {
  return withApiErrorReporting("serverCreateSectionInvite", async () => {
    const response = await serverApi.post<SectionInviteType>(
      `${SERVER_API_PATH}/auth/section_invite/`,
      { section_id: sectionId },
    );
    return response.data;
  });
}

export async function serverDeleteSectionInvite(inviteId: string): Promise<void> {
  return withApiErrorReporting("serverDeleteSectionInvite", async () => {
    await serverApi.delete(`${SERVER_API_PATH}/auth/section_invite/${inviteId}/`);
  });
}

export async function serverRedeemInviteKey(inviteKey: string) {
  return withApiErrorReporting("serverRedeemInviteKey", async () => {
    const ret = await serverApi.post(
      `${SERVER_API_PATH}/auth/section_invite/redeem_for_existing_user/?invite_key=${inviteKey}`,
    );
    return ret.data as { section_id: string; user_id: string };
  });
}

export async function serverRegisterUserWithInviteKey(
  inviteKey: string,
  userData: UserInformationType & {
    password: string;
    password_confirm: string;
    eula_accepted: boolean;
  },
) {
  return withApiErrorReporting("serverRegisterUserWithInviteKey", async () => {
    const ret = await serverApi.post(
      `${SERVER_API_PATH}/auth/section_invite/redeem_for_new_user/?invite_key=${inviteKey}`,
      userData,
    );
    return ret.data as UserType;
  });
}

export async function serverGetSectionMetadataFromInviteKey(inviteKey: string) {
  return withApiErrorReporting("serverGetSectionMetadataFromInviteKey", async () => {
    const ret = await serverApi.get(
      `${SERVER_API_PATH}/auth/section_invite/get_section_metadata/?invite_key=${inviteKey}`,
    );
    return ret.data as InviteKeySectionType;
  });
}

export async function serverExportSection(sectionId: string): Promise<JSON> {
  return withApiErrorReporting("serverExportSection", async () => {
    const results = await serverApi.post<JSON>(
      `${SERVER_API_PATH}/material/sections/${sectionId}/export_external/`,
    );

    return results.data;
  });
}

export async function serverImportExternalSection(
  formData: FormData,
): Promise<ImportExternalSectionResponseType> {
  return withApiErrorReporting("serverImportExternalSection", async () => {
    const results = await serverApi.post<ImportExternalSectionResponseType>(
      `${SERVER_API_PATH}/material/sections/import_external/`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return results.data;
  });
}

export async function serverCloneSection(
  section_id: string,
  course_id?: string,
  term_id?: string,
  asTemplate?: boolean,
): Promise<void> {
  const data: { [key: string]: string | boolean } = {};
  if (course_id) {
    data.target_course_id = course_id;
  }
  if (term_id) {
    data.target_term_id = term_id;
  }
  if (asTemplate) {
    data.as_template = true;
  }

  return withApiErrorReporting("serverCloneSection", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/${section_id}/clone/`, data);
  });
}

export async function serverImportSection(data: ImportSectionRequestType): Promise<void> {
  return withApiErrorReporting("serverImportSection", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/import/`, data);
  });
}

export async function serverUpsertPageAccessData(
  data: Partial<PageAccessDataType>,
): Promise<PageAccessDataType | undefined> {
  try {
    const response = await serverApi.post<PageAccessDataType>(
      `${SERVER_API_PATH}/material/page_access_data/upsert/`,
      data,
    );
    const item = response.data as PageAccessDataType & { section_version?: number };

    // Match createPubsubSlice behavior: store the model, but drop section_version.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { section_version, ...pageAccessData } = item;
    store.dispatch({ type: "page_access_data/updateOne", payload: pageAccessData });

    return pageAccessData;
  } catch (error) {
    reportApiFailure("serverUpsertPageAccessData", error);
    return undefined;
  }
}

export async function serverUnlinkSection(sectionId: string): Promise<void> {
  return withApiErrorReporting("serverUnlinkSection", async () => {
    const response = await serverApi.post(
      `${SERVER_API_PATH}/material/sections/${sectionId}/unlink/`,
    );
    store.dispatch(slices.sections.actions.updateOne(response.data));
  });
}

export async function serverDisconnectSection(
  sectionId: string,
  deletePagesAndAssessments: boolean,
): Promise<void> {
  return withApiErrorReporting("serverDisconnectSection", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/sections/${sectionId}/disconnect/`, {
      delete_pages_and_assessments: deletePagesAndAssessments,
    });
  });
}

export async function serverCopyTopic(topicId: string, sectionIds: string[]): Promise<void> {
  return withApiErrorReporting("serverCopyTopic", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/topics/${topicId}/copy/`, {
      section_ids: sectionIds,
    });
  });
}

export async function serverAddCoursesToGrouping(
  groupingId: string,
  courseIds: string[],
): Promise<void> {
  return withApiErrorReporting("serverAddCoursesToGrouping", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/course_groupings/${groupingId}/add_courses/`,
      { course_ids: courseIds },
    );
  });
}

export async function serverMoveActivityWithinSection(
  activityId: string,
  topicId: string,
): Promise<void> {
  const data = { topic_id: topicId };

  return withApiErrorReporting("serverMoveActivityWithinSection", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/activities/${activityId}/move/`, data);
  });
}

export async function serverCopyActivity(activityId: string): Promise<void> {
  return withApiErrorReporting("serverCopyActivity", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/activities/${activityId}/copy/`);
  });
}

export async function serverMovePageWithinSection(
  pageId: string,
  category: string,
  topicId?: string,
): Promise<void> {
  const data: { category: string; topic_id?: string } = { category };
  if (topicId) {
    data.topic_id = topicId;
  }

  return withApiErrorReporting("serverMovePageWithinSection", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/move/`, data);
  });
}

export async function serverCopyPage(
  pageId: string,
  sectionIds?: string[],
  activityIds?: string[],
): Promise<void> {
  const data: { section_ids?: string[]; activity_ids?: string[] } = {};
  if (sectionIds && sectionIds.length > 0) {
    data.section_ids = sectionIds;
  }

  if (activityIds && activityIds.length > 0) {
    data.activity_ids = activityIds;
  }

  return withApiErrorReporting("serverCopyPage", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/copy/`, data);
  });
}

export async function serverGetLicodeToken(
  username: string,
  role: string,
  roomName: string,
  licodeUrl: string,
): Promise<string> {
  return withApiErrorReporting("serverGetLicodeToken", async () => {
    const data = {
      username,
      role,
      room_name: roomName,
      licode_nuve_server: licodeUrl,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/licodetoken/`, data);
    return results.data;
  });
}

export async function serverGetOpentokToken(classSessionId: string) {
  return withApiErrorReporting("serverGetOpentokToken", async () => {
    const data = {
      class_session_id: classSessionId,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/opentoktoken/`, data);
    logInfoWithDebugFlag("debug_virtual_class", "[serverGetOpentokToken] results", results.data);
    const resultData = results.data as unknown as {
      "api-key": string;
      token: string;
    };
    return {
      token: resultData.token,
      apiKey: resultData["api-key"],
    };
  });
}

export async function serverGetDailyToken(userId: string, roomName: string) {
  return withApiErrorReporting("serverGetDailyToken", async () => {
    const data = {
      user_id: userId,
      room_name: roomName,
    };
    const results = await serverApi.post<string>(`${SERVER_API_PATH}/av/dailytoken/`, data);
    if (results.status !== 200) {
      throw new Error("Failed to get daily token");
    }
    const resultData = results.data as unknown as {
      token: string;
    };
    return {
      token: resultData.token,
      apiKey: "dailyApiKey",
    };
  });
}

export async function serverClearPageGroupMemberships(pageId: string): Promise<void> {
  return withApiErrorReporting("serverClearPageGroupMemberships", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/clear_group_memberships/`);
  });
}

export async function serverDistributeUsersToGroups(
  pageId: string,
  sectionUserIds: string[] | null,
): Promise<void> {
  return withApiErrorReporting("serverDistributeUsersToGroups", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/distribute/`, {
      section_user_ids: sectionUserIds,
    });
  });
}

export async function serverReleaseAssessments(pageId: string): Promise<void> {
  return withApiErrorReporting("serverReleaseAssessments", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/release_assessments/`);
  });
}

export async function serverBulkCreateSubmissions(
  page_id: string,
  lock_page = false,
): Promise<void> {
  return withApiErrorReporting("serverBulkCreateSubmissions", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/submissions/bulk/`, { page_id, lock_page });
  });
}

export async function serverRaiseHand(classUserId: string): Promise<ClassUserType> {
  return withApiErrorReporting("serverRaiseHand", async () => {
    const response = await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/raise-hand/`,
    );
    return response.data;
  });
}

export async function serverLowerHand(classUserId: string): Promise<ClassUserType> {
  return withApiErrorReporting("serverLowerHand", async () => {
    const response = await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/lower-hand/`,
    );
    return response.data;
  });
}

export async function serverClearVotes(classSessionId: string): Promise<void> {
  return withApiErrorReporting("serverClearVotes", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/clear-votes/`,
    );
  });
}

export async function serverEndSession(classSessionId: string): Promise<void> {
  return withApiErrorReporting("serverEndSession", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/end-session/`,
    );
  });
}

export async function serverMoveOutcome(
  outcomeId: string,
  destination: { group_id: string } | { subgroup_id: string },
): Promise<OutcomeType> {
  return withApiErrorReporting("serverMoveOutcome", async () => {
    const response = await serverApi.post<OutcomeType>(
      `${SERVER_API_PATH}/material/outcomes/${outcomeId}/move/`,
      destination,
    );
    return response.data;
  });
}

export async function serverMoveOutcomeSubgroup(
  outcomeSubgroupId: string,
  groupId: string,
): Promise<OutcomeSubgroupType> {
  return withApiErrorReporting("serverMoveOutcomeSubgroup", async () => {
    const response = await serverApi.post<OutcomeSubgroupType>(
      `${SERVER_API_PATH}/material/outcome_subgroups/${outcomeSubgroupId}/move/`,
      { group_id: groupId },
    );
    return response.data;
  });
}

// New streamlined bulk import endpoints
export async function serverGetBulkImportSignedUrl(
  importType: BulkImportType,
  filename: string,
): Promise<{ upload_url: string; s3_key: string }> {
  return withApiErrorReporting("serverGetBulkImportSignedUrl", async () => {
    const results = await serverApi.post<{ upload_url: string; s3_key: string }>(
      `${SERVER_API_PATH}/material/bulk_import_jobs/get-signed-url/`,
      {
        import_type: importType,
        filename,
      },
    );

    return results.data;
  });
}

export async function serverStartBulkImportJob(
  importType: BulkImportType,
  s3Key: string,
  originalFilename: string,
  forceSystemCheck = false,
): Promise<{ message: string; import_job_id: string }> {
  return withApiErrorReporting("serverStartBulkImportJob", async () => {
    const results = await serverApi.post<{ message: string; import_job_id: string }>(
      `${SERVER_API_PATH}/material/bulk_import_jobs/start-import/`,
      {
        import_type: importType,
        s3_key: s3Key,
        original_filename: originalFilename,
        require_system_check: forceSystemCheck,
      },
    );

    return results.data;
  });
}

export async function serverUpdateScreensharing(
  classSessionId: string,
  screensharingClassUserId: string | null,
  breakoutGroupId?: string | null,
): Promise<void> {
  return withApiErrorReporting("serverUpdateScreensharing", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/class_sessions/update-screensharing/`, {
      class_session_id: classSessionId,
      screensharing_class_user_id: screensharingClassUserId,
      breakout_group_id: breakoutGroupId,
    });
  });
}

export async function serverCreateBreakouts(
  classSessionId: string,
  breakoutGroupType: "random" | "page",
  breakoutDuration: number,
  groupPageId: string | null,
  groupCount?: number,
  shouldSyncBreakoutsWithPageGroups?: boolean,
): Promise<void> {
  return withApiErrorReporting("serverCreateBreakoutGroups", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/class_sessions/create-breakouts/`, {
      class_session_id: classSessionId,
      breakout_group_type: breakoutGroupType,
      breakout_duration: breakoutDuration,
      group_page_id: groupPageId,
      group_count: groupCount,
      should_sync_breakouts_with_page_groups: shouldSyncBreakoutsWithPageGroups,
    });
  });
}

export async function serverEndBreakouts(classSessionId: string): Promise<void> {
  return withApiErrorReporting("serverEndBreakouts", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/end-breakouts/`,
    );
  });
}

export async function serverExtendBreakoutTime(
  classSessionId: string,
  add_duration: number,
): Promise<void> {
  return withApiErrorReporting("serverExtendBreakoutTime", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/extend-breakout-time/`,
      { add_duration },
    );
  });
}

export async function serverUpdateTimerDuration(
  classSessionId: string,
  durationSeconds: number,
): Promise<void> {
  return withApiErrorReporting("serverUpdateTimer", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/material/class_sessions/${classSessionId}/update-timer-duration/`,
      {
        duration_seconds: durationSeconds,
      },
    );
  });
}

export async function serverUpdateClassUserBreakoutGroup(
  classUserId: string,
  breakoutGroupId: string | undefined | null,
): Promise<void> {
  return withApiErrorReporting("serverUpdateClassUserBreakoutGroup", async () => {
    await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/update-breakout-group/`,
      {
        breakout_group_id: breakoutGroupId,
      },
    );
  });
}

export async function serverListenToBreakoutGroup(
  classUserId: string,
  listenedBreakoutGroupId: string | null,
): Promise<void> {
  return withApiErrorReporting("serverListenToBreakoutGroup", async () => {
    await serverApi.post<ClassUserType>(
      `${SERVER_API_PATH}/material/class_users/${classUserId}/listen-to-breakout-group/`,
      {
        listened_breakout_group_id: listenedBreakoutGroupId,
      },
    );
  });
}

export type CreateNewUserRequestType = {
  email: string;
  first_name: string;
  last_name?: string;
  password?: string;
  confirm_password?: string;
  send_onboarding_email: boolean;
};

export async function serverCreateNewUser(
  userData: CreateNewUserRequestType,
): Promise<{ id: string }> {
  return withApiErrorReporting("serverCreateNewUser", async () => {
    const response = await serverApi.post<{ id: string }>(
      `${SERVER_API_PATH}/orgs/org_users/create_new_user/`,
      userData,
    );

    return response.data;
  });
}

export async function serverStartRecordingSession(sectionId: string): Promise<void> {
  return withApiErrorReporting("serverStartRecordingSession", async () => {
    await serverApi.post(`${SERVER_API_PATH}/recording/recording_sessions/start_recording/`, {
      section_id: sectionId,
    });
  });
}

export async function serverMarkRecordingSessionStarted(recordingSessionId: string): Promise<void> {
  return withApiErrorReporting("serverMarkRecordingSessionStarted", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/mark_started/`,
    );
  });
}

// NOTE: serverMarkRecordingSessionStarted is never called from within the monarch application, but it is called by
// the recording agent (see https://github.com/minervaproject/recording-agent/blob/development/worker/interpreter.js),
// which is why we expose it as a global on the window object here. See also the interface declaration for this in types.ts.
window.serverMarkRecordingSessionStarted = serverMarkRecordingSessionStarted;

export async function serverStopRecordingSession(recordingSessionId: string): Promise<void> {
  return withApiErrorReporting("serverStopRecordingSession", async () => {
    await serverApi.post(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/stop_recording/`,
    );
  });
}

export async function serverGenerateRecordingUrl(
  recordingSessionId: string,
  setup: boolean,
): Promise<string> {
  return withApiErrorReporting("serverGenerateRecordingUrl", async () => {
    const action = setup ? "generate_setup_url" : "generate_recording_url";
    const response = await serverApi.get<string>(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/${action}/`,
    );
    return response.data;
  });
}

export async function serverGenerateTranscriptUrl(
  recordingSessionId: string,
): Promise<string | null> {
  return withApiErrorReporting("serverGenerateTranscriptUrl", async () => {
    const response = await serverApi.get<string | null>(
      `${SERVER_API_PATH}/recording/recording_sessions/${recordingSessionId}/generate_transcript_url/`,
    );
    return response.data;
  });
}

// In general, we should use storeApi for updates (including partial updates) and NOT this method.
// This should only be used from within components/monitoring/SectionsMonitoringTable.tsx
export async function serverUpdateSectionForMonitoring(
  sectionId: string,
  sectionData: Partial<SectionType>,
): Promise<SectionType> {
  return withApiErrorReporting("serverUpdateSectionForMonitoring", async () => {
    const response = await serverApi.patch<SectionType>(
      `${SERVER_API_PATH}/material/sections/${sectionId}/`,
      sectionData,
    );
    return response.data;
  });
}

export async function serverGetBugReportSignedUrl(
  filename: string,
  contentType: string,
): Promise<{ upload_url: string; s3_key: string; file_url: string }> {
  return withApiErrorReporting("serverGetBugReportSignedUrl", async () => {
    const response = await serverApi.post<{ upload_url: string; s3_key: string; file_url: string }>(
      `${SERVER_API_PATH}/bug_report/reports/get-signed-url/`,
      {
        filename,
        content_type: contentType,
      },
    );
    return response.data;
  });
}

export async function serverSubmitBugReport(data: {
  subject: string;
  description: string;
  diagnostics?: Record<string, unknown> | null;
  section_id?: string | null;
  class_session_id?: string | null;
  submission_url?: string | null;
  user_uploaded_files_urls?: string[] | null;
}): Promise<void> {
  return withApiErrorReporting("serverSubmitBugReport", async () => {
    await serverApi.post(`${SERVER_API_PATH}/bug_report/reports/`, data);
  });
}

export async function serverSubmitSystemCheckResult(data: {
  check_end_result: "passed" | "failed";
  test_results: PreclassCheckResultType;
  browser: string | null;
  os: string | null;
}) {
  return withApiErrorReporting("serverSubmitCheckResult", async () => {
    await serverApi.post(`${SERVER_API_PATH}/system_check/check_results/`, data);
  });
}

export async function serverCopyPageGroups(pageId: string, sourcePageId: string): Promise<void> {
  return withApiErrorReporting("serverCopyPageGroups", async () => {
    await serverApi.post(`${SERVER_API_PATH}/material/pages/${pageId}/copy_groups/`, {
      source_page_id: sourcePageId,
    });
  });
}
